import { Seq } from 'immutable';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { ViewModelService } from 'src/app/shared/view-model.service';
import { RuleMatchType, WaypointType } from 'src/graphql/generated/graphql.generated';
import { RequestViewModel } from 'src/models/request-view-model';
import { selectRendezvousRequestByRequestLocationId } from 'src/store/entityStores/rendezvous-request-selectors';
import { ActiveAlertRecord } from 'src/store/generated/active-alert-record.generated';
import { DiversionReasonRecord } from 'src/store/generated/diversion-reason-record.generated';
import { MedicalFacilityUnitRecord } from 'src/store/generated/medical-facility-unit-record.generated';
import { PhysicianRecord } from 'src/store/generated/physician-record.generated';
import { hasLatLng } from 'src/store/model-helpers';
import { NavigationLink } from 'src/util/angular/ng-navigation-fx';
import { undefinedOrSwitchMap } from 'src/util/rxjs/operators';
import { filterNull } from 'src/util/rxjs/operators/filter-null';
import {
  RequestLocationRecord,
  RequestLocationViewModelBase,
} from '../store/generated/request-location-record.generated';
import { FboViewModel } from './fbo-view-model';
import { LocationHazardViewModel } from './location-hazard-view-model';
import { LocationViewModel } from './location-view-model';
import { MedicalFacilityViewModel } from './medical-facility-view-model';
import { RendezvousRequestViewModel } from './rendezvous-request-view-model';
import { pickupOrDropoffDescription, ruleMatchType } from './waypoint-view-model';

export class RequestLocationViewModel extends RequestLocationViewModelBase {
  public rendezvousRequest$: Observable<RendezvousRequestViewModel | undefined>;
  public readonly fbo$: Observable<FboViewModel | undefined>;
  public readonly displayName$: Observable<string>;
  public readonly medicalFacility$: Observable<MedicalFacilityViewModel | undefined>;
  public readonly hazards$: Observable<Seq.Indexed<LocationHazardViewModel>>;
  public readonly alerts$: Observable<Seq.Indexed<ActiveAlertRecord>>;
  public readonly medicalFacilityUnit$: Observable<MedicalFacilityUnitRecord | undefined>;
  public readonly diversionReason$: Observable<DiversionReasonRecord | undefined>;
  public readonly physician$: Observable<PhysicianRecord | undefined>;
  public readonly physicianName$: Observable<string | undefined>;

  get patientLocation() {
    const location = this.roomNumber != null ? 'Room ' + this.roomNumber : '';
    return this.type !== WaypointType.ReceivingFacility ? this.abbreviation : location;
  }

  get hasLatLng() {
    return hasLatLng(this);
  }

  get pickupDropoffDescription(): 'Pickup' | 'Dropoff' | undefined {
    return pickupOrDropoffDescription(this.type);
  }

  get ruleMatchType(): RuleMatchType {
    return ruleMatchType(this.type);
  }

  constructor(
    requestLocationRecord: RequestLocationRecord,
    public readonly request: RequestViewModel,
    public readonly location: LocationViewModel | undefined,
    private _model: ViewModelService,
  ) {
    super(requestLocationRecord);
    this.alerts$ = request.card.request$.pipe(
      switchMap(() => request.card.getAllActiveAlerts(this.ruleMatchType)),
    );
    this.editNavigationLink$ = this.request.card.trip$.pipe(
      undefinedOrSwitchMap((trip) => trip.getWaypointByType(this.type)),
      map((wp) =>
        wp == null
          ? undefined
          : this._model.nav.currentCompany.ops
              .card(this.request.card.cardNumber)
              .trip.editWaypoint(wp.id),
      ),
    );

    this.rendezvousRequest$ = this._getRendezvousRequest();
    this.medicalFacility$ =
      this.medicalFacilityId != null
        ? this._model.getMedicalFacilityById(this.medicalFacilityId)
        : of(undefined);

    this.medicalFacilityUnit$ =
      this.medicalFacilityUnitId != null
        ? this._model.getMedicalFacilityUnitById(this.medicalFacilityUnitId)
        : of(undefined);

    this.physician$ =
      this.physicianId != null ? this._model.getPhysicianById(this.physicianId) : of(undefined);

    this.physicianName$ = this.physicianName != null ? of(this.physicianName) : of(undefined);

    const locationHazards$ =
      location != null ? location.hazards$ : of(Seq.Indexed<LocationHazardViewModel>());
    const medicalFacilityHazards$ = this.medicalFacility$.pipe(
      switchMap((mf) =>
        mf != null && mf.location != null
          ? mf.location.hazards$
          : of(Seq.Indexed<LocationHazardViewModel>()),
      ),
    );

    this.hazards$ = combineLatest([locationHazards$, medicalFacilityHazards$]).pipe(
      map(([locHazards, medFacilityHazards]) => {
        return locHazards
          .concat(medFacilityHazards)
          .groupBy((h) => h.id)
          .map((v) => v.first<LocationHazardViewModel>())
          .valueSeq();
      }),
    );

    this.fbo$ = this.fboId == null ? of(undefined) : this._model.getFboById(this.fboId);

    this.displayName$ =
      this.medicalFacilityId != null
        ? this.medicalFacility$.pipe(
            filterNull(),
            map((r) => {
              return r.name !== this.name
                ? `${r.abbreviation} <span class="request-location-lz-name">(LZ-${this.abbreviation})</span>`
                : r.abbreviation;
            }),
          )
        : of(this.abbreviation);

    this.diversionReason$ =
      this.diversionReasonId == null
        ? of(undefined)
        : this._model.diversionReasons.getById(this.diversionReasonId);
  }

  get canArrangeTransport() {
    return !this.request.card.isReadOnly;
  }

  public editNavigationLink$: Observable<NavigationLink | undefined>;

  private _getRendezvousRequest() {
    return this._model.store.select(selectRendezvousRequestByRequestLocationId(this.id)).pipe(
      filterNull(),
      map((rr) => new RendezvousRequestViewModel(rr, this._model.store)),
    );
  }
}
